import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, TrendingUp, DollarSign, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingSuggestionModal = ({ lead, onClose }) => {
  const { admin, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState(null);
  const [showTotalWithEmbassy, setShowTotalWithEmbassy] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  useEffect(() => {
    fetchPricingSuggestion(false);
  }, [lead]);

  const fetchPricingSuggestion = async (applyDiscount = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(
        `${API}/admin/pricing-rules/suggest/${lead.id}?apply_discount=${applyDiscount}`,
        { headers: getAuthHeader() }
      );
      
      setSuggestion(response.data);
      setDiscountApplied(applyDiscount && response.data.discount_applied);
    } catch (error) {
      console.error('Failed to fetch pricing suggestion:', error);
      setError(error.response?.data?.detail || 'Failed to fetch pricing suggestion');
    } finally {
      setLoading(false);
      setApplyingDiscount(false);
    }
  };

  const handleApplyDiscount = async () => {
    setApplyingDiscount(true);
    await fetchPricingSuggestion(true);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getConfidenceBadge = (confidence) => {
    const styles = {
      high: { bg: '#dcfce7', color: '#166534', label: 'High Confidence' },
      medium: { bg: '#fef3c7', color: '#92400e', label: 'Medium Confidence' },
      low: { bg: '#fee2e2', color: '#991b1b', label: 'Low Confidence' }
    };
    
    const style = styles[confidence] || styles.low;
    
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '24px', color: '#059669' }}>₹</span>
              Pricing Suggestion
            </h2>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
              {lead.name} - {lead.service_type} to {lead.display_country || lead.destination_country}
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p>Loading pricing suggestion...</p>
            </div>
          ) : error ? (
            <div style={{ 
              padding: '20px', 
              borderRadius: '8px', 
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca'
            }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'start' }}>
                <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
                <div>
                  <h3 style={{ margin: 0, marginBottom: '8px', color: '#991b1b' }}>Unable to fetch pricing</h3>
                  <p style={{ margin: 0, color: '#991b1b' }}>{error}</p>
                </div>
              </div>
            </div>
          ) : suggestion ? (
            <div>
              {/* Suggested Price Card - Visaasure Charges Only */}
              <div style={{
                padding: '24px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                color: 'white',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, marginBottom: '8px', opacity: 0.9 }}>Your Service Quote (Visaasure Charges)</p>
                <h1 style={{ margin: 0, fontSize: '48px', fontWeight: '700' }}>
                  {formatCurrency(suggestion.visaasure_charges_total)}
                </h1>
                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                    For {suggestion.num_applicants} applicant{suggestion.num_applicants > 1 ? 's' : ''}
                  </p>
                  {getConfidenceBadge(suggestion.confidence)}
                </div>
                
                {/* Show bulk discount if applied */}
                {suggestion.discount_applied && suggestion.discount_amount > 0 && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '8px 12px', 
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    🎉 <strong>{suggestion.discount_applied}% Bulk Discount Applied!</strong>
                    <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>
                      Saved: {formatCurrency(suggestion.discount_amount)}
                    </div>
                  </div>
                )}
                
                {/* Bulk discount button or message */}
                {!discountApplied && suggestion.num_applicants > 1 && (
                  <div style={{ marginTop: '12px' }}>
                    {suggestion.discount_eligible ? (
                      <button
                        onClick={handleApplyDiscount}
                        disabled={applyingDiscount}
                        style={{
                          background: 'rgba(255, 215, 0, 0.9)',
                          color: '#78350f',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: '6px',
                          cursor: applyingDiscount ? 'wait' : 'pointer',
                          fontSize: '13px',
                          fontWeight: '700',
                          width: '100%',
                          opacity: applyingDiscount ? 0.6 : 1
                        }}
                      >
                        {applyingDiscount ? '⏳ Applying...' : '🎁 Apply Bulk Discount'}
                      </button>
                    ) : (
                      <div style={{
                        padding: '10px 16px',
                        borderRadius: '6px',
                        background: 'rgba(255, 255, 255, 0.15)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        fontSize: '13px',
                        textAlign: 'center',
                        fontWeight: '600'
                      }}>
                        ⭐ This is already our best quote!
                      </div>
                    )}
                  </div>
                )}
                
                {/* Toggle to show total with embassy fees */}
                <div style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <button
                    onClick={() => setShowTotalWithEmbassy(!showTotalWithEmbassy)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      border: '1px solid rgba(255, 255, 255, 0.4)',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    {showTotalWithEmbassy ? '🔽 Hide' : '▶️ Show'} Total Customer Payment (with Embassy Fees)
                  </button>
                  
                  {showTotalWithEmbassy && (
                    <div style={{ marginTop: '12px', animation: 'fadeIn 0.3s' }}>
                      <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>Total Customer Payment (including embassy fees)</p>
                      <p style={{ margin: 0, fontSize: '24px', fontWeight: '600', marginTop: '4px' }}>
                        {formatCurrency(suggestion.suggested_total_price)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Breakdown */}
              {(suggestion.embassy_fees_total || suggestion.vfs_bls_charges_total || suggestion.visaasure_charges_total) && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '16px', 
                  borderRadius: '8px',
                  backgroundColor: '#fefce8',
                  border: '1px solid #fde047'
                }}>
                  <h3 style={{ margin: 0, marginBottom: '12px', color: '#854d0e', fontWeight: '600' }}>
                    💰 Price Breakdown
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    
                    {/* Embassy Fees */}
                    {suggestion.embassy_fees_total > 0 && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '6px',
                        border: '1px solid #bae6fd'
                      }}>
                        <div>
                          <p style={{ margin: 0, fontWeight: '600', color: '#0c4a6e' }}>Embassy Fees</p>
                          <p style={{ margin: 0, fontSize: '11px', color: '#075985' }}>
                            {formatCurrency(suggestion.embassy_fees_total / suggestion.num_applicants)} × {suggestion.num_applicants} applicant{suggestion.num_applicants > 1 ? 's' : ''}
                          </p>
                          <p style={{ margin: 0, fontSize: '10px', color: '#7dd3fc', marginTop: '2px' }}>
                            (Customer pays to government)
                          </p>
                        </div>
                        <p style={{ margin: 0, fontWeight: '700', fontSize: '18px', color: '#0c4a6e' }}>
                          {formatCurrency(suggestion.embassy_fees_total)}
                        </p>
                      </div>
                    )}

                    {/* VFS/BLS Charges */}
                    {suggestion.vfs_bls_charges_total > 0 && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #fde68a'
                      }}>
                        <div>
                          <p style={{ margin: 0, fontWeight: '600', color: '#78350f' }}>
                            VFS/BLS Charges 
                            <span style={{ 
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: '#fbbf24',
                              color: '#78350f',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '700'
                            }}>PAY LATER</span>
                          </p>
                          <p style={{ margin: 0, fontSize: '12px', color: '#92400e' }}>
                            {formatCurrency(suggestion.vfs_bls_charges_total / suggestion.num_applicants)} × {suggestion.num_applicants} applicant{suggestion.num_applicants > 1 ? 's' : ''}
                          </p>
                        </div>
                        <p style={{ margin: 0, fontWeight: '700', fontSize: '18px', color: '#78350f' }}>
                          {formatCurrency(suggestion.vfs_bls_charges_total)}
                        </p>
                      </div>
                    )}

                    {/* Visaasure Charges */}
                    {suggestion.visaasure_charges_total > 0 && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '12px',
                        backgroundColor: '#ecfdf5',
                        borderRadius: '6px',
                        border: '2px solid #10b981'
                      }}>
                        <div>
                          <p style={{ margin: 0, fontWeight: '600', color: '#065f46' }}>
                            Visaasure Service Charges
                            <span style={{ 
                              marginLeft: '8px',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background: '#10b981',
                              color: 'white',
                              fontSize: '11px',
                              fontWeight: '700'
                            }}>YOUR QUOTE</span>
                          </p>
                          <p style={{ margin: 0, fontSize: '11px', color: '#059669', marginTop: '2px' }}>
                            To be collected at signup
                          </p>
                        </div>
                        <p style={{ margin: 0, fontWeight: '700', fontSize: '22px', color: '#059669' }}>
                          {formatCurrency(suggestion.visaasure_charges_total)}
                        </p>
                      </div>
                    )}

                  </div>
                </div>
              )}

              {/* Market Comparison */}
              {suggestion.market_avg_price && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '16px', 
                  borderRadius: '8px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd'
                }}>
                  <h3 style={{ margin: 0, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TrendingUp size={18} style={{ color: '#0284c7' }} />
                    Market Context
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Market Average</p>
                      <p style={{ margin: 0, fontWeight: '600', color: '#0c4a6e' }}>
                        {formatCurrency(suggestion.market_avg_price)}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Lowest</p>
                      <p style={{ margin: 0, fontWeight: '600', color: '#0c4a6e' }}>
                        {formatCurrency(suggestion.market_min_price)}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Highest</p>
                      <p style={{ margin: 0, fontWeight: '600', color: '#0c4a6e' }}>
                        {formatCurrency(suggestion.market_max_price)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Super Admin Details */}
              {admin?.role === 'super_admin' && suggestion.total_cost && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '16px', 
                  borderRadius: '8px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a'
                }}>
                  <h3 style={{ margin: 0, marginBottom: '12px', color: '#92400e' }}>
                    💼 Cost & Margin (Super Admin Only)
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#92400e' }}>Total Cost</p>
                      <p style={{ margin: 0, fontWeight: '600', color: '#78350f' }}>
                        {formatCurrency(suggestion.total_cost)}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#92400e' }}>Profit Margin</p>
                      <p style={{ margin: 0, fontWeight: '600', color: '#78350f' }}>
                        {formatCurrency(suggestion.margin_amount)}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#92400e' }}>Margin %</p>
                      <p style={{ margin: 0, fontWeight: '600', color: '#059669', fontSize: '18px' }}>
                        {suggestion.margin_percent}%
                      </p>
                      <p style={{ margin: 0, fontSize: '10px', color: '#92400e', marginTop: '2px' }}>
                        on actual costs
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div style={{ 
                padding: '12px', 
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                fontSize: '12px',
                color: '#666'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <CheckCircle2 size={14} style={{ color: '#059669' }} />
                  <span>Last Updated: {formatDate(suggestion.last_updated)}</span>
                </div>
                <p style={{ margin: 0, marginTop: '8px' }}>
                  💡 <strong>Tip:</strong> This price is optimized based on market data and internal costs. 
                  You can use this as a reference when creating proposals for this lead.
                </p>
              </div>
            </div>
          ) : null}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingSuggestionModal;
